import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * TEC-508. Modeled directly on AuthContext.tsx: every code-defined experiment's variant is
 * resolved server-side once per render path (see server/vike-handler.ts's `experiments` field,
 * server/experiments/resolve-active-experiment-variants.ts) and threaded down as an explicit
 * prop into this provider, never read via `usePageContext()`/`useData()` inside a leaf
 * component. That distinction matters in this codebase specifically: several island routes
 * (job/SRP/salary/salary-role/locations/best-companies-dimension) build their React tree by
 * hand and never wrap it in vike-react's own page-context provider, so a component that called
 * `usePageContext()` directly would silently get `undefined` there. Explicit-prop threading
 * (mirroring how `initialUser` reaches `AuthProvider` on every one of those routes) works
 * identically everywhere.
 */

interface ExperimentEntry {
  value: string;
  /**
   * True only when this entry came from a render path's own `initialVariants` prop, as opposed
   * to being merged in afterward by the fallback-fetch path via `setVariant`. This is a field on
   * the same entry as its value (not a separate parallel set) so the two can never drift apart:
   * a fallback fetch already logs its own exposure server-side (see get-variant.ts's
   * `logExposure` default), so `useExperiment` must NOT treat a fetch-populated entry as
   * "SSR-provided" too: that would double-log the same exposure once server-side (from the
   * fetch request itself) and again client-side (from the exposure beacon this hook would
   * otherwise fire on seeing a now-defined entry).
   */
  isSsrProvided: boolean;
}

export interface ExperimentContextValue {
  /** experimentKey -> entry, for every key either resolved server-side or learned via the client fallback fetch. */
  entries: Record<string, ExperimentEntry>;
  /** Merges a client-resolved variant in (the fetch fallback for a key SSR didn't provide); never marked isSsrProvided. */
  setVariant: (key: string, variant: string) => void;
  /**
   * Claims this experiment key's exposure for the provider's lifetime: returns true only the
   * first time it's called for a given key, false on every call after. Multiple components
   * reading the same experiment key on one page (e.g. one SavedSearchAlertToggle per saved
   * search on the "My Jobs" page) must log exactly one exposure for that subject, not one per
   * mounted component. This is the shared gate that guarantees that, since dedup lives on the
   * provider rather than in each hook instance's own ref.
   */
  claimExposure: (key: string) => boolean;
}

const ExperimentContext = createContext<ExperimentContextValue | undefined>(undefined);

export interface ExperimentProviderProps {
  initialVariants: Record<string, string>;
  children: React.ReactNode;
}

export function ExperimentProvider({ initialVariants, children }: ExperimentProviderProps) {
  const [entries, setEntries] = useState<Record<string, ExperimentEntry>>(() =>
    Object.fromEntries(Object.entries(initialVariants).map(([key, value]) => [key, { value, isSsrProvided: true }]))
  );
  const exposureClaimedKeysRef = useRef<Set<string>>(new Set());

  // Merge in new SSR-provided entries on client-side navigation to another page (merged, not
  // replaced), so navigating to a static-owned page (which never resolves experiments server-side,
  // see vike-handler.ts's isStaticOwnedPath guard) doesn't blank out variants this render path
  // already knows, exactly mirroring how AuthContext's initialUser sync avoids clearing an
  // already-known session on a page whose pageContext has no user.
  useEffect(() => {
    const incomingKeys = Object.keys(initialVariants);
    if (incomingKeys.length === 0) return;
    setEntries((prev) => {
      const next = { ...prev };
      for (const key of incomingKeys) {
        next[key] = { value: initialVariants[key], isSsrProvided: true };
      }
      return next;
    });
    // Only the keys/values matter for merge-in timing, not object identity across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialVariants)]);

  const setVariant = useCallback((key: string, variant: string) => {
    setEntries((prev) =>
      prev[key]?.value === variant ? prev : { ...prev, [key]: { value: variant, isSsrProvided: false } }
    );
  }, []);

  const claimExposure = useCallback((key: string) => {
    if (exposureClaimedKeysRef.current.has(key)) return false;
    exposureClaimedKeysRef.current.add(key);
    return true;
  }, []);

  const value = useMemo<ExperimentContextValue>(
    () => ({ entries, setVariant, claimExposure }),
    [entries, setVariant, claimExposure]
  );

  return <ExperimentContext.Provider value={value}>{children}</ExperimentContext.Provider>;
}

export interface UseExperimentResult {
  /** "control" until resolved, and for anyone not exposed to a running experiment: render the default experience for this value. */
  variant: string;
  /** True once a real value is known, either provided by SSR (immediately true) or the fallback fetch has resolved. */
  ready: boolean;
}

/**
 * Reads an experiment's variant with zero flash whenever the current render path already
 * resolved it server-side (the common case, see resolve-active-experiment-variants.ts, which
 * resolves every code-defined experiment on every non-static-owned page). Falls back to a
 * one-time client fetch only for a key that SSR didn't provide: practically, a key added to
 * experiment-definitions.ts after this render path's bundle shipped, or a request to a
 * static-owned route, which deliberately never resolves experiments server-side to stay
 * cacheable. That fallback still flashes "control" briefly, same as before this fix; it just
 * no longer applies to the common case.
 *
 * Also responsible for logging the real "exposure" event: the SSR-side bulk resolution never
 * does (see resolve-active-experiment-variants.ts's doc comment: it runs on every page, most of
 * which don't render this experiment's UI at all), so this hook fires a one-time exposure beacon
 * whenever a component actually mounts and uses an SSR-provided value, exactly where "someone
 * really saw this variant" is true. The fallback-fetch path already logs exposure server-side as
 * part of that same on-demand request. The one-time-ness is enforced by the provider's
 * `claimExposure`, not a ref local to this hook: multiple components reading the same key on one
 * page (see ExperimentContextValue.claimExposure's doc comment) must still log only one exposure.
 */
export function useExperiment(experimentKey: string): UseExperimentResult {
  const ctx = useContext(ExperimentContext);
  const entry = ctx?.entries[experimentKey];
  const ssrVariant = entry?.value;
  const isTrulySsrProvided = entry?.isSsrProvided ?? false;
  const [fetchedVariant, setFetchedVariant] = useState<string | null>(null);
  const fetchStartedRef = useRef(false);

  useEffect(() => {
    if (!isTrulySsrProvided) return;
    if (!ctx?.claimExposure(experimentKey)) return;
    fetch(`/api/experiments/${encodeURIComponent(experimentKey)}/exposure`, { method: "POST" }).catch(() => {
      // Fire-and-forget: a lost exposure beacon undercounts by one, not worth retry complexity.
    });
  }, [experimentKey, isTrulySsrProvided, ctx]);

  useEffect(() => {
    if (ssrVariant !== undefined) return; // SSR already provided it, nothing to fetch.
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;

    let cancelled = false;
    fetch(`/api/experiments/${encodeURIComponent(experimentKey)}/variant`)
      .then((res) => (res.ok ? res.json() : { variant: "control" }))
      .then((data: { variant?: string }) => {
        if (cancelled) return;
        const variant = data.variant ?? "control";
        setFetchedVariant(variant);
        ctx?.setVariant(experimentKey, variant);
      })
      .catch(() => {
        if (!cancelled) setFetchedVariant("control");
      });
    return () => {
      cancelled = true;
    };
  }, [experimentKey, ssrVariant, ctx]);

  if (ssrVariant !== undefined) {
    return { variant: ssrVariant, ready: true };
  }
  return { variant: fetchedVariant ?? "control", ready: fetchedVariant !== null };
}

/** Returns experiment context or undefined when outside ExperimentProvider. Use when hook order must stay stable. */
export function useExperimentContextOptional(): ExperimentContextValue | undefined {
  return useContext(ExperimentContext);
}

export { ExperimentContext };
